<template>
  <div>
    <Modal contentWidth="700px" :required="true" @close="$emit('close')">
      <template #header>
        <p class="fs-20 font-weight-bold ml-1 mb-0" style="line-height: 0.675rem; color: #163D7D;">
          Submitals del proyecto
        </p>
      </template>

      <template #body>
        <div class="d-flex align-items-center mb-2">
          <b-input v-model="search" placeholder="Buscar por código o nombre" />
        </div>

        <div class="items-box">
          <div v-if="loading" class="text-center text-info py-4">
            <b-spinner small type="grow"></b-spinner>
            <span class="pl-2">Cargando submitals...</span>
          </div>

          <div v-else-if="loadError" class="text-center text-danger py-4">
            No se pudieron cargar los submitals.<br>
            <b-link @click="fetchItems">Reintentar</b-link>
          </div>

          <div v-else-if="visibleItems.length === 0" class="text-center text-muted py-4 fs-15">
            No hay submitals para mostrar
          </div>

          <div
            v-for="item in visibleItems"
            v-else
            :key="itemKey(item)"
            class="item-row d-flex align-items-center"
          >
            <span class="item-number fs-13">{{ item.submital_no }}</span>

            <span class="item-title fs-13 text-truncate" :class="{ 'text-muted': !item.name }" :title="item.name">
              {{ item.name || 'Sin nombre' }}
            </span>

            <span v-b-tooltip.hover :title="photosTooltip(item)" class="item-photos fs-12" :class="photosClass(item)">
              <font-awesome-icon :icon="['far', 'image']" size="lg" fixed-width />
              <span class="item-count">{{ item.completed_photos_count }}/{{ item.required_photos_count }}</span>
            </span>

            <span class="item-progress fs-13" :class="item.progress >= 100 ? 'text-success' : 'text-danger'">
              <span class="item-count">{{ item.progress }}%</span>
              <font-awesome-icon
                :icon="item.progress >= 100 ? ['fas', 'shield-check'] : ['fas', 'exclamation-triangle']"
                :class="item.progress >= 100 ? 'text-success' : 'text-warning'"
                fixed-width
              />
            </span>

            <span v-if="isAdded(item)" class="item-action" :title="canRemove ? '' : 'La solicitud debe tener al menos un submital'">
              <b-button
                variant="outline-danger"
                size="sm"
                block
                :disabled="!canRemove"
                @click="openConfirm(item, 'remove')"
              >
                Quitar
              </b-button>
            </span>
            <span v-else class="item-action">
              <b-button variant="outline-primary" size="sm" block @click="openConfirm(item, 'add')">
                Agregar
              </b-button>
            </span>
          </div>
        </div>
      </template>

      <template #footer>
        <span></span>
      </template>
    </Modal>

    <ConfirmModal
      v-if="pending"
      :required="true"
      :disabled="processing"
      :zIndex="1100"
      @close="closeConfirm"
      @onConfirm="confirmAction"
    >
      <template #header>
        <h5 class="mb-0">{{ pending.action === 'add' ? 'Agregar submital' : 'Quitar submital' }}</h5>
      </template>
      <template #body>
        <font-awesome-icon :icon="['far', 'exclamation-triangle']" size="2x" class="text-warning mb-3" fixed-width />
        <p class="fs-16 mb-2">
          ¿Desea {{ pending.action === 'add' ? 'agregar' : 'quitar' }}
          <strong>{{ pending.item.submital_no }}</strong>
          {{ pending.action === 'add' ? 'a' : 'de' }} esta solicitud?
        </p>
        <p class="text-muted fs-14 mb-0 text-clamp-3">{{ pending.item.name || 'Sin nombre' }}</p>
        <p v-if="processing" class="text-info fs-14 mt-3 mb-0">
          <b-spinner small class="mr-1" />{{ pending.action === 'add' ? 'Agregando...' : 'Quitando...' }}
        </p>
        <b-alert v-if="actionError" show variant="danger" class="fs-14 mt-3 mb-0 py-2">{{ actionError }}</b-alert>
      </template>
    </ConfirmModal>
  </div>
</template>

<script>
import Modal from '../../shared/Modal'
import ConfirmModal from '../../shared/ConfirmModal'

export default {
  name: 'DrawingRequestItemsModal',
  components: { Modal, ConfirmModal },
  props: {
    drawingRequest: { type: Object, required: true },
    cachedItems: { type: Array, default: null }
  },
  data () {
    return {
      items: this.cachedItems || [],
      loading: false,
      loadError: false,
      search: '',
      pending: null,
      processing: false,
      actionError: null
    }
  },
  computed: {
    addedItemIds () {
      const ids = {}
      ;(this.drawingRequest.drawing_request_items || []).forEach((item) => {
        ids[`${item.itemable_type}:${item.itemable_id}`] = item.id
      })
      return ids
    },
    addedCount () {
      return Object.keys(this.addedItemIds).length
    },
    canRemove () {
      return this.addedCount > 1
    },
    visibleItems () {
      const term = this.search.trim().toLowerCase()

      return this.items.filter((item) => {
        if (!term) return true
        return `${item.submital_no} ${item.name || ''}`.toLowerCase().includes(term)
      })
    }
  },
  mounted () {
    if (!this.cachedItems) this.fetchItems()
  },
  methods: {
    fetchItems () {
      this.loading = true
      this.loadError = false

      this.$http.get('/api/production/drawing_requests/available_items', {
        params: { project_id: this.drawingRequest.project.id }
      })
        .then((res) => {
          this.items = res.data.items
          this.$emit('loaded', this.items)
        })
        .catch(() => {
          this.loadError = true
        })
        .finally(() => {
          this.loading = false
        })
    },
    itemKey (item) {
      return `${item.itemable_type}:${item.itemable_id}`
    },
    isAdded (item) {
      return !!this.addedItemIds[this.itemKey(item)]
    },
    openConfirm (item, action) {
      this.actionError = null
      this.pending = { item, action }
    },
    closeConfirm () {
      if (this.processing) return
      this.pending = null
      this.actionError = null
    },
    confirmAction () {
      if (this.processing) return
      this.processing = true
      this.actionError = null

      const request = this.pending.action === 'add' ? this.addItem() : this.removeItem()

      request
        .then(() => {
          this.pending = null
        })
        .catch((error) => {
          const data = (error.response && error.response.data) || {}
          this.actionError = data.error || data.message || 'Ocurrió un error'
        })
        .finally(() => {
          this.processing = false
        })
    },
    addItem () {
      const { itemable_type: itemableType, itemable_id: itemableId } = this.pending.item

      return this.$http.post(`/api/production/drawing_requests/${this.drawingRequest.id}/items`, {
        drawing_request_item: { itemable_type: itemableType, itemable_id: itemableId }
      }).then((res) => {
        this.$emit('item-added', res.data)
      })
    },
    removeItem () {
      const itemId = this.addedItemIds[this.itemKey(this.pending.item)]

      return this.$http.delete(`/api/production/drawing_requests/${this.drawingRequest.id}/items/${itemId}`)
        .then((res) => {
          this.$emit('item-removed', res.data)
        })
    },
    photosClass (item) {
      if (item.required_photos_count === 0) return 'text-muted'
      return item.completed_photos_count === item.required_photos_count ? 'text-success' : 'text-danger'
    },
    photosTooltip (item) {
      if (item.required_photos_count === 0) return 'No requiere fotos'

      const { completed_photos_count: completed, required_photos_count: required } = item
      return completed === required
        ? 'Fotos completas'
        : `${completed} de ${required} fotos requeridas`
    }
  }
}
</script>

<style scoped>
.items-box {
  border: 1px solid #dee2e6;
  border-radius: 0.25rem;
  max-height: 360px;
  overflow-y: auto;
}
.item-row {
  padding: 0.45rem 0.75rem;
  border-bottom: 1px solid #f1f1f1;
  gap: 0.5rem;
}
.item-row:last-child {
  border-bottom: none;
}
.item-number {
  width: 70px;
  flex-shrink: 0;
  color: #6c757d;
}
.item-title {
  flex: 1;
  min-width: 0;
}
.item-photos {
  width: 48px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
}
.item-progress {
  width: 59px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.25rem;
}
.item-action {
  width: 80px;
  flex-shrink: 0;
}
.item-count {
  font-variant-numeric: tabular-nums;
}
</style>
